import { useDispatch, useSelector } from 'react-redux';
import { Button, StandAloneLink, Surface, Txt } from '@vst/beam';
import { OpenInNew } from '@vst/beam-icons/icons';
import { useTranslation } from 'react-i18next';

import { ADDON_KIND, VOICE_ADDONS_KINDS } from '@app/constants';
import { SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';
import { MyAddonsCard, MyAddonsError } from '@app/components/MyAddonsCard';
import { useAccountStatus, useNavigate, useScreenResolution, useShowAddonsShop } from '@app/hooks';
import { openInNewTab, selectAddOns, selectHasAddons } from '@app/redux';

import { ProductInstanceStatus } from '@mfe/shared/schema-types';

import { NoAddons } from './no-add-ons';
import { getActionLabelByKind } from './utils';

import styles from './styles.module.scss';

export const MyAddons = (): JSX.Element | null => {
  const { t } = useTranslation(['Overview', 'Global']);
  const dispatch = useDispatch();

  const { loading: currentAddOnsLoading, currentAddOns, error } = useSelector(selectAddOns);
  const showAddonsShop = useShowAddonsShop();
  const { isAccountPreInstall, accountStatus } = useAccountStatus();

  const { isSmall, isExtraSmall } = useScreenResolution();
  const isMobile = isSmall || isExtraSmall;

  const hasAddOns = useSelector(selectHasAddons);
  const numberOfAddOns = currentAddOns?.length || 0;

  const containerClass = `${styles['addons-container']} ${numberOfAddOns > 1 ? '' : styles['single-column']}`;

  if (!showAddonsShop && !hasAddOns) {
    return null;
  }

  if (currentAddOnsLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <MyAddonsError
        title={t('Profile:addOns.title')}
        description={t('Profile:addOns.error.description')}
        refreshButtonLabel={t('Profile:addOns.error.refreshButton')}
      />
    );
  }

  return (
    <Surface data-cy="addons-container" className={styles['container']}>
      <div className={styles['header']}>
        <Txt variant="bodyLargeBold">{t('addOns.title')}</Txt>
        {!isMobile && !isAccountPreInstall && (
          <div className={styles['header-buttons']}>
            <MyAddonsButtons />
          </div>
        )}
      </div>

      {hasAddOns ? (
        <>
          <Surface className={containerClass}>
            {currentAddOns?.map((addon, index) => {
              const isVoice = VOICE_ADDONS_KINDS.includes(addon.kind);
              const showAction = isVoice || addon.kind === ADDON_KIND.CYBER_PROTECT;
              const actionLabel = getActionLabelByKind(addon.kind, t);
              const isDisabled = accountStatus !== ProductInstanceStatus.Active;
              // Fallback for backwards compatibility on older accounts that do not have the externalLink property populated on Voice addons
              const voiceExternalLink = addon.externalLink || t('addOns.alianzaLink');
              const externalLink = isVoice ? voiceExternalLink : addon.externalLink;

              return (
                <div key={index} className={styles['addonCard']}>
                  <MyAddonsCard addon={addon}>
                    {showAction && externalLink && (
                      <StandAloneLink
                        data-cy={`${addon.kind}-action`}
                        variant="secondary"
                        icon={OpenInNew}
                        iconPos="right"
                        href=""
                        style={{ textDecoration: 'none' }}
                        disabled={isDisabled}
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          e.preventDefault();
                          if (!isDisabled) {
                            dispatch(openInNewTab(externalLink));
                          }
                        }}
                      >
                        {actionLabel}
                      </StandAloneLink>
                    )}
                  </MyAddonsCard>
                </div>
              );
            })}
          </Surface>
          {isMobile && !isAccountPreInstall && (
            <div className={styles['header-buttons']}>
              <MyAddonsButtons />
            </div>
          )}
        </>
      ) : (
        <NoAddons />
      )}
    </Surface>
  );
};

const MyAddonsButtons = () => {
  const { t } = useTranslation(['Overview', 'Global']);
  const { goTo } = useNavigate();

  const hasAddOns = useSelector(selectHasAddons);

  if (!hasAddOns) {
    return null;
  }

  return (
    <>
      <Button
        variant="tertiary"
        onClick={(e) => {
          e.preventDefault();
          sessionStorageManager.setItem(SESSION_STORAGE_VARIABLES.SHOW_PLAN_TAB, true);
          goTo('Profile');
        }}
      >
        {t('Overview:addOns.manageAddonsButtonLabel')}
      </Button>

      <Button
        variant="secondary"
        onClick={(e) => {
          e.preventDefault();
          goTo('ShopAddons');
        }}
      >
        {t('Overview:addOns.shopAddonsButtonLabel')}
      </Button>
    </>
  );
};

const Loading = () => {
  return (
    <Surface data-cy="loading" className={styles['loading-container']}>
      <div className={styles['loading-title']} />
      <div className={styles['loading-addon-section']}>
        <div className={styles['icon']} />
        <div className={styles['name']} />
        <div className={styles['desc']} />
      </div>
      <div className={styles['loading-footer']} />
    </Surface>
  );
};
